import { useForm } from 'react-hook-form';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import {
  Button,
  Container,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  TextField as MuiTextField,
  Box
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dateFormatter, dropdownFetch, formatDate, getCurrentDateTime, getKeyPathValue, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { DischargeSummaryMedicineDetailsForm } from '../../HMS/transactions/DischargeSummaryMedicineDetailsForm';
import { DiseaseDetailsForm } from './DiseaseDetailsForm';
import { AllergyDetailsForm } from './AllergyDetailsForm';
import { GeneralExaminationDetailsForm } from './GeneralExaminationdetailsForm';
import { ClinicalExamDetailsForm } from './ClinicalExamDetailsForm';
import { FamilyHistoryDetailsForm } from './FamilyHistoryDetailsForm';
import { InvestigationBookingDetailsForm } from './InvestigationBookingDetailsForm';
import { ComplaintsDetailForm } from './ComplaintsDetailsForm';

const PATIENT_TABLE_HEADER = [
  { header: 'UHID', accessor: 'uhid' },
  { header: 'OP No', accessor: 'op_no' },
  { header: 'Created At', accessor: 'createdAt' },
  { header: 'Patient Name', accessor: (row) => `${row.name_suffix} ${row.first_name} ${row.last_name}` },
  { header: 'Mobile No', accessor: 'address.mobile' },
  { header: 'Doctor', accessor: 'consultant.name' },
  { header: 'Department', accessor: 'department.department' },
  { header: 'Address', accessor: 'address.address' }
];

const TABLE_HEADER = [
  {
    header: ' Date',
    accessor: (row) => `${formatDate(row?.date)}`
  },
  {
    header: 'UHID No',
    accessor: 'uhid'
  },
  {
    header: 'Patient Name',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row?.patient?.name_suffix} ${row?.patient?.first_name} ${row?.patient?.last_name}`}
        </span>
      );
    }
  },
  {
    header: 'Sponsor',
    accessor: (row)=> row?.patient?.sponsor?.name
  },
  {
    header: 'Mobile No',
    accessor: (row)=> row?.patient?.address?.mobile
  },
];

const COMPLAINT_DETAIL_HEADER = [
  {
    header: 'Complaint List',
    accessor: 'complaint_type'
  },
  {
    header: 'Duration',
    accessor: 'duration'
  },
];

const ALLERGY_DETAIL_HEADER = [
  {
    header: 'Allergy Type',
    accessor: 'allergy_type'
  },
  {
    header: 'Description',
    accessor: 'description'
  }
];

const GENERAL_EXAM_DETAIL_HEADER = [
  {
    header: 'Exam Type',
    accessor: 'exam_type'
  },
  {
    header: 'Description',
    accessor: 'description'
  }
];

const PAST_GENERAL_EXAM_DETAIL_HEADER = [
  {
    header: 'Exam Type',
    accessor: 'exam_type'
  },
  {
    header: 'Description',
    accessor: 'description'
  }
];

const CLINICAL_EXAM_DETAIL_HEADER = [
  {
    header: 'Exam Type',
    accessor: 'exam_type'
  },
  {
    header: 'Description',
    accessor: 'description'
  }
];

const PAST_CLINICAL_EXAM_DETAIL_HEADER = [
  {
    header: 'Exam Type',
    accessor: 'exam_type'
  },
  {
    header: 'Description',
    accessor: 'description'
  }
];

const FAMILY_HISTORY_DETAIL_HEADER = [
  {
    header: 'Disease',
    accessor: 'disease'
  },
  {
    header: 'Relation',
    accessor: 'relation'
  },
  {
    header: 'Description',
    accessor: 'description'
  }
];

const PAST_FAMILY_HISTORY_DETAIL_HEADER = [
  {
    header: 'Disease',
    accessor: 'disease'
  },
  {
    header: 'Relation',
    accessor: 'relation'
  },
  {
    header: 'Description',
    accessor: 'description'
  }
];


const PAST_ALLERGY_DETAIL_HEADER = [
  {
    header: 'Allergy Type',
    accessor: 'allergy_type'
  },
  {
    header: 'Description',
    accessor: 'description'
  }
];

const DISEASE_DETAIL_HEADER = [
  {
    header: 'Disease Type',
    accessor: 'disease_type'
  },
  {
    header: 'Description',
    accessor: 'description'
  },
];

const PAST_HISTORY_DISEASE = [
  {
    header: 'Disease',
    accessor: (row) => row?.disease_type
  },
  {
    header: 'Date',
    accessor: 'createdAt'
  }
];

const PAST_HISTORY_COMPLAINTS = [
  {
    header: 'Complaints',
    accessor: (row) => row?.complaint_type
  },
  {
    header: 'Date',
    accessor: 'createdAt'
  }
];



const INVESTIGATION_DETAIL_HEADER = [
  {
    header: 'Service Name',
    accessor: (row)=> row?.serviceName || row?.service_name
  },
  {
    header: 'Type',
    accessor: 'type'
  },
  {
    header: 'Operating Doctor',
    accessor: 'doctor'
  },
  {
    header: 'Qty',
    accessor: 'quantity'
  },
  {
    header: 'Rate',
    accessor: 'rate'
  },
  {
    header: 'Amount',
    accessor: 'amount'
  },
  {
    header: 'Disc Amt',
    accessor: 'disc_amt'
  },
  {
    header: 'Net Amount',
    accessor: 'net_amt'
  }
];


const PAST_INVESTIGATION_DETAIL_HEADER = [
  {
    header: 'Service Name',
    accessor: 'service_name'
  },
  {
    header: 'Type',
    accessor: 'type'
  },
  {
    header: 'Operating Doctor',
    accessor: 'doctor'
  },
  {
    header: 'Qty',
    accessor: 'quantity'
  },
  {
    header: 'Rate',
    accessor: 'rate'
  },
  {
    header: 'Amount',
    accessor: 'amount'
  },
  {
    header: 'Disc Amt',
    accessor: 'disc_amt'
  },
  {
    header: 'Net Amount',
    accessor: 'net_amt'
  }
];

const MEDICINE_DETAIL_HEADER = [
  {
    header: 'Medicine Name',
    accessor: (row) => row?.medicine?.main?.name || row?.medicine || row?.medicine_name
  },
  {
    header: 'Unit',
    accessor: 'unit'
  },
  {
    header: 'Frequency',
    accessor: (row) => row?.frequency?.frequency || row?.frequency || row?.frequency_name
  },
  {
    header: 'Days',
    accessor: 'days'
  },
  {
    header: 'Morning',
    accessor: 'morning'
  },
  {
    header: 'Noon',
    accessor: 'noon'
  },
  {
    header: 'Evening',
    accessor: 'evening'
  },
  {
    header: 'Night',
    accessor: 'night'
  },
  {
    header: 'Date',
    accessor: 'createdAt'
  }
];

const PAST_MEDICINE_DETAIL_HEADER = [
  {
    header: 'Medicine Name',
    accessor: (row) => row?.medicine_name
  },
  {
    header: 'Unit',
    accessor: 'unit'
  },
  {
    header: 'Frequency',
    accessor: (row) => row?.frequency_name
  },
  {
    header: 'Days',
    accessor: 'days'
  },
  {
    header: 'Morning',
    accessor: 'morning'
  },
  {
    header: 'Noon',
    accessor: 'noon'
  },
  {
    header: 'Evening',
    accessor: 'evening'
  },
  {
    header: 'Night',
    accessor: 'night'
  },
  {
    header: 'Date',
    accessor: 'createdAt'
  }
];

const initialPatientDataObj = {
  data: [],
  loading: false
};

export const IVFForm = () => {
  const doctorIDRef = useRef();
  const sponsorIDRef = useRef();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [patientDataObj, setPatientDataObj] = useState(initialPatientDataObj);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [searchPatientRegisterationDialog, setSearchPatientRegisterationDialog] = useState(false);
  const [patientDetailList, setPatientDetailList] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [complaints, setComplaints] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [allergy, setAllergy] = useState([]);
  const [generalExam, setGeneralExam] = useState([]);
  const [clinicalExam, setClinicalExam] = useState([]);
  const [familyHistory, setFamilyHistory] = useState([]);
  const [investigation, setInvestigation] = useState([]);
  const [departmentDropdown, setDepartmentDropdown] = useState([]);
  const [medicineDetailList, setmedicineDetailList] = useState([]);
  const [addComplaintDialogueOpen, setAddComplaintDialogueOpen] = useState(false);
  const [addAlleryDialogueOpen, setAddAllergyDialogueOpen] = useState(false);
  const [generalExamDialogueOpen, setGeneralExamDialogueOpen] = useState(false);
  const [clinicalExamDialogueOpen, setClinicalExamDialogueOpen] = useState(false);
  const [familyHistoryDialogueOpen, setFamilyHistoryDialogueOpen] = useState(false);
  const [addDiseaseDialogueOpen, setAddDiseaseDialogueOpen] = useState(false);
  const [investigationDialogOpen, setInvestigationDialogOpen] = useState(false);
  const [diagnos, setDiagnos] = useState([]);
  const [addMedicineDialogue, setAddMedicineDialogue] = useState(false);
  const [patientVitals, setPatientVitals] = useState([]);
  const [input, setInput] = useState('');
  const [pastComplaints, setPastComplaints] = useState([]);
  const [pastDiagnos, setPastDiagnos] = useState([]);
  const [pastService, setPastService] = useState([]);
  const [pastMedicine, setPastMedicine] = useState([]);
  const [medicine, setMedicine] = useState([]);
  const [frequency, setFrequency] = useState([]);
  const [dose, setDose] = useState([]);
  const [diseaseHistory,setDiseaseHistory] = useState([])
  const [complaintHistory,setComplaintHistory] = useState([])
  const [allergyHistory,setAllergyHistory] = useState([])
  const[generalExamHistory,setGeneralExamHistory] = useState([])
  const [clinicalExamHistory,setClinicalExamHistory] = useState([])
  const [investigationHistory,setInvestigationHistory] = useState([])
  const [prescriptionHistory,setPrescriptionHistory] = useState([])
  const[pastFamilyHistory,setPastFamilyHistory] = useState([])
  const [queryInput,setQueryInput] = useState("")

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    const dayDifference = today.getDate() - birthDate.getDate();
    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--;
    }
    return age;
  };

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        uhid: Yup.string().required(' ')
      })
    ),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      time: getCurrentDateTime('time'),
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  const getDepartmentDropdown = useCallback(async () => {
    const data = await dropdownFetch('department');
    setDepartmentDropdown(data);
  }, []);

  const getMedicineDropdown = useCallback(async () => {
    const data = await dropdownFetch('product-master');
    setMedicine(data);
  }, []);

  const getFrequencyDropdown = useCallback(async () => {
    const data = await dropdownFetch('frequency-master');
    setFrequency(data);
  }, []);

  const getDoseDropdown = useCallback(async () => {
    const data = await dropdownFetch('dose-master');
    setDose(data);
  }, []);

  useEffect(() => {
    getDepartmentDropdown();
    getMedicineDropdown();
    getFrequencyDropdown();
    getDoseDropdown();
  }, [getDepartmentDropdown, getMedicineDropdown, getFrequencyDropdown, getDoseDropdown]);

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, 'bill', [
        '__v',
        '_id',
        'createdAt',
        'status',
        'service_items',
        'uhid',
        'complaints',
        'diagnos',
        'medicine',
        'vital'
      ]);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      setValue('uhid', selectedRecord.uhid);
      setValue('patient_name', `${selectedRecord.patient?.name_suffix} ${selectedRecord.patient?.first_name} ${selectedRecord.patient?.last_name}`);
      const formattedRegDate = selectedRecord?.date ? new Date(selectedRecord.date).toISOString().split('T')[0] : '';
      setValue('date', formattedRegDate);
      setValue('patient_age', calculateAge(selectedRecord?.patient?.dob))
      setValue('mobile', (selectedRecord?.patient?.address?.mobile))
      setValue('doctor', (selectedRecord?.patient?.consultant?.name))
      setValue('department', (selectedRecord?.patient?.department?.department))
      sponsorIDRef.current = selectedRecord?.patient?.sponsor?._id;
      setDiseases(selectedRecord.disease_items);
      setComplaints(selectedRecord.complaint_items);
      setAllergy(selectedRecord?.allergy_items)
      setGeneralExam(selectedRecord?.generalexam_items)
      setClinicalExam(selectedRecord?.clinicalexam_items)
      setInvestigation(selectedRecord?.investigation_items)
      setmedicineDetailList(selectedRecord?.prescription_items)
      setFamilyHistory(selectedRecord?.family_history)
      setDiagnos(selectedRecord.diagnos);
    } else {
      reset();
    }
  }, [selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/ivf-patient-history-update' : '/ivf-patient-history-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: {
        ...formValues,
        disease_items: diseases,
        complaint_items:complaints,
        allergy_items: allergy,
        generalexam_items: generalExam,
        clinicalexam_items: clinicalExam,
        investigation_items:investigation,
        prescription_items:medicineDetailList,
        family_history: familyHistory
      }
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'updated' : 'created'} IVF Patient History`, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
      reset();
    } else {
      toast(`Failed to ${editFlag ? 'update' : 'create'} IVF Patient History`, {
        variant: 'error'
      });
    }
    setLoading(false);
  };

  const handleRowDelete = (rowId) => {
    setDetailList((prev) =>
      prev.filter((data,index) => {
        return index !== rowId;
      })
    );
  };

  const handleRowAllergyDelete = (rowId) => {
    setAllergy((prev) =>
      prev.filter((data,index) => {
        return index !== rowId;
      })
    );
  };

  const handleRowGeneralExamDelete = (rowId) => {
    setGeneralExam((prev) =>
      prev.filter((data,index) => {
        return index !== rowId;
      })
    );
  };

  const handleRowClinicalExamDelete = (rowId) => {
    setClinicalExam((prev) =>
      prev.filter((data,index) => {
        return index !== rowId;
      })
    );
  };

  const handleRowFamilyHistoryDelete = (rowId) => {
    setFamilyHistory((prev) =>
      prev.filter((data,index) => {
        return index !== rowId;
      })
    );
  };

  const handleDiseaseDelete = (rowId) => {
    console.log(rowId,"HELLO DISEASE ROW ID ")
    setDiseases((prev) =>
      prev.filter((data,index) => {
        return index !== rowId;
      })
    );
  };

  const handleComplaintDelete = (rowId) => {
    setComplaints((prev) =>
      prev.filter((data,index) => {
        return index !== rowId;
      })
    );
  };

  const handleDeleteInvestigation = (rowId) => {
    setInvestigation((prev) =>
      prev.filter((data,index) => {
        return index !== rowId;
      })
    );
  };

  const handleDeleteMedicine = (rowId) => {
    setmedicineDetailList((prev) =>
      prev.filter((data,index) => {
        return index !== rowId;
      })
    );
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/ivf-patient-history-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted IVF Patient History', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to deleted IVF Patient History', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/ivf-patient-history-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully cancel IVF Patient History', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to cancel IVF Patient History', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setPatientDataObj({ loading: true });
    const limit =10
    const query = {limit:limit}
    const response = await request('/ivf-patient-history-all', { method: 'GET', query: query});
    if (response.success) {
      // const sortedData = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      // const filteredData = sortedData.filter(
      //   (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      // );
      setPatientDataObj({ loading: false, data: response.data});
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setPatientDataObj({ loading: false });
    }
  };

  const searchPatientHandler = async (uhid = null) => {
    if (typeof uhid !== 'number' && typeof uhid !== 'string') uhid = null;
    setLoading(true);
    const today = new Date().toISOString().split('T')[0];
    if (!uhid) setSearchPatientRegisterationDialog(true);
    const response = await request('/patient-registeration-gynae-list', {
      method: 'GET',
      query: { uhid }
    });
    if (response?.success) {
      if (uhid) {
        const {
          data: [patientData]
        } = response;
        setValue('visit', patientData.visit_no);
        setValue('op', patientData.op_no);
        setValue('patient_name', `${patientData.first_name} ${patientData.last_name}`);
        setValue('patient_suffix', patientData.name_suffix);
        setValue('opd_type', patientData.opd_type?.type);
        setValue('ref_type', patientData.ref_by?.referral_name);
        setValue('application_no', patientData.app_no_date);
        setValue('doctor', patientData.consultant?.name);
        doctorIDRef.current = patientData.consultant?._id;
        setValue('department', patientData.department?.department);
        setValue('patient_age', calculateAge(patientData?.dob));
        setValue('sponsor', patientData.sponsor?.name);
        setValue('remarks', patientData.remarks);
        setValue('queue_no', patientData.app_no_date);
        setValue('card_no', patientData.chq_card_no);
        setValue('vertical', patientData.vertical?.verticalname);
      } else {
        // Remove the filtering to get all the data
        setPatientDetailList(response.data);
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleActiveTab = (newTabIndex) => {
    setActiveTab(newTabIndex);
  };

  const onDetailAddHandlers = (data) => {
    const existingDetail = investigation.find(({ service }) => service === data.service);
    if (existingDetail) {
      toast('Service already chosen', { variant: 'error' });
      return;
    }
    const serviceName = data?.serviceName;
    setInvestigation((prev) => [
      ...prev,
      {
        ...data,
        service_name: serviceName
      }
    ]);
    toast('Service Added Successfully', { variant: 'success' });
  };

  const onAddDetailHandler = (row) => {
    const existingDetail = medicineDetailList.find(({ medicine_name }) => medicine_name === row.medicine_name);
    if (existingDetail) {
      toast('Medicine already chosen', { variant: 'error' });
      return;
    }
    const medicineData = medicine.find(({ _id }) => _id === row.medicine_name);
    const frequencyData = frequency.find(({ _id }) => _id === row.frequency);
    const productName = medicineData?.main?.name;
    const medicineId = medicineData?._id;
    const frequencyName = frequencyData?.frequency;
    const frequencyId = frequencyData?._id;
    if (!productName) {
      toast('Product name not found', { variant: 'error' });
      return;
    }
    setmedicineDetailList((prev) => [
      ...prev,
      {
        medicine: productName,
        medicine_name: medicineId,
        frequency: frequencyName,
        frequency_name: frequencyId,
        unit: row.unit,
        days: row?.days,
        frequencyId: frequencyId,
        morning: row.morning,
        noon: row.noon,
        evening: row.evening,
        night: row.night
      }
    ]);
  };

  const searchHistoryDetails = async (uhid) => {
    setLoading(true);
    const response = await request('/ivf-patient-history-detail', {
      method: 'GET',
      params: uhid
    });
    if (response?.success) {
      const { data } = response;
      if (!data) {
        toast('No patient found', { variant: 'warning' });
      } else {
      setDiseaseHistory(data?.disease_items)
      setComplaintHistory(data?.complaint_items)
     setAllergyHistory(data?.allergy_items)
     setGeneralExamHistory(data?.generalexam_items)
     setClinicalExamHistory(data?.clinicalexam_items)
     setInvestigationHistory(data?.investigation_items)
     setPrescriptionHistory(data?.prescription_items)
     setPastFamilyHistory(data?.family_history)
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };


  useEffect(()=>{
    handleSearch(queryInput)
  },[queryInput])

  const handleSearch = async () => {
    const lowercasedInput = queryInput.toLowerCase().trim();
    const limit = 10
    try {
      const query = {limit:limit};
      if (lowercasedInput) {
        query.name = lowercasedInput;
      }
      const response = await request('/ivf-patient-history-all', {
        method: 'GET',
        query: query
      });
      if (response.success) {
        setPatientDataObj({ loading: false, data: response.data });
      } else {
        toast('Error in fetching data', { variant: 'error' });
        setPatientDataObj({ loading: false });
      }
    } catch (error) {
      console.error('Error during search:', error);
      toast('Error during search', { variant: 'error' });
      setPatientDataObj({ loading: false, data: [] });
    }
  };

  const handleQueryChange=(e)=>{
    setQueryInput(e.target.value)
  }


  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 700 }}>
                IVF Examination
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    setDetailList([]);
                    reset();
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  onClick={() => window.open(`/print${pathname}?refId=${selectedRecord._id}`, '_blank')}
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  onClick={() => window.open(`/print${pathname}/progress-report?refId=${watch('uhid')}`, '_blank')}
                  disabled={!selectedRecord || currentAction}
                >
                  Progress Report
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn id={'Exit'} variant="contained">
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={4}
                  sx={{ display: 'flex', gap: 0.3, flexDirection: 'column', boxShadow: '0px 2px 2px #0099ff' }}
                >
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 600, fontSize: '1.4rem' }}>Visit No</Typography>
                    <TextField
                      sx={{ flex: 0.7 }}
                      name="visit_no"
                      disabled
                  
                    />
                    <Button
                      sx={{ flex: 0.1, minWidth: 0, p: 0.25, height: '100%' }}
                      variant="contained"
                      onClick={searchPatientHandler}
                      disabled={!['new', 'edit'].includes(currentAction)}
                    >
                      <SearchIcon />
                    </Button>
                  </Stack>
                  <Stack direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.21, fontWeight: 600, fontSize: '1.4rem' }}>Date</Typography>
                    <TextField sx={{ flex: 0.8 }} name="date" type="date" disabled />
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{ display: 'flex', gap: 0.3, flexDirection: 'column', boxShadow: '0px 2px 2px #0099ff' }}
                >
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 600, fontSize: '1.4rem' }}>Doc No./Date</Typography>
                    <TextField disabled={true} sx={{ flex: 0.4 }} placeholder="Doc No." name="doc_no" />
                    <TextField disabled={true} sx={{ flex: 0.4 }} placeholder="Bill No." name="date" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 600, fontSize: '1.4rem' }}>Patient/Age</Typography>
                    <TextField disabled={true} name="patient_name" sx={{ flex: 0.4 }} placeholder="Patient Name" />
                    <TextField disabled={true} name="patient_age" sx={{ flex: 0.4 }} placeholder="Age" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 600, fontSize: '1.4rem' }}>Doctor</Typography>
                    <TextField disabled={true} sx={{ flex: 0.8 }} name="doctor" placeholder="Doctor" />
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{ display: 'flex', gap: 0.3, flexDirection: 'column', boxShadow: '0px 2px 2px #0099ff' }}
                >
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 600, fontSize: '1.4rem' }}>UHID No</Typography>
                    <TextField sx={{ flex: 0.8 }} name="uhid" placeholder="Search By Uhid " disabled />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 600, fontSize: '1.4rem' }}>Op/Mobile No.</Typography>
                    <TextField disabled={true} sx={{ flex: 0.4 }} name="op" placeholder="Op No." />
                    <TextField disabled={true} sx={{ flex: 0.4 }} name="mobile" placeholder="Mobile No." />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 600, fontSize: '1.4rem' }}>Department</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} name="department" placeholder="Department" />
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Button
                      variant="contained"
                      disabled={!['new', 'edit'].includes(currentAction)}
                      onClick={() => handleActiveTab(0)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 800,
                        backgroundColor: activeTab === 0 ? 'primary.main' : 'default',
                        color: activeTab === 0 ? 'white' : 'text.primary'
                      }}
                    >
                      History
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!['new', 'edit'].includes(currentAction)}
                      onClick={() => handleActiveTab(1)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 800,
                        backgroundColor: activeTab === 1 ? 'primary.main' : 'default',
                        color: activeTab === 1 ? 'white' : 'text.primary'
                      }}
                    >
                      Disease
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!['new', 'edit'].includes(currentAction)}
                      onClick={() => handleActiveTab(2)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 800,
                        backgroundColor: activeTab === 2 ? 'primary.main' : 'default',
                        color: activeTab === 2 ? 'white' : 'text.primary'
                      }}
                    >
                      Complaints
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(3)}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 800,
                        backgroundColor: activeTab === 3 ? 'primary.main' : 'default',
                        color: activeTab === 3 ? 'white' : 'text.primary'
                      }}
                    >
                      Allergy
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(4)}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{
                        backgroundColor: activeTab === 4 ? 'primary.main' : 'default',
                        color: activeTab === 4 ? 'white' : 'text.primary',
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 800
                      }}
                    >
                      General Exam
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!['new', 'edit'].includes(currentAction)}
                      onClick={() => handleActiveTab(5)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 800,
                        backgroundColor: activeTab === 5 ? 'primary.main' : 'default',
                        color: activeTab === 5 ? 'white' : 'text.primary'
                      }}
                    >
                      Clinical Exam
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(6)}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 800,
                        backgroundColor: activeTab === 6 ? 'primary.main' : 'default',
                        color: activeTab === 6 ? 'white' : 'text.primary'
                      }}
                    >
                      Investigation
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(7)}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 800,
                        backgroundColor: activeTab === 7 ? 'primary.main' : 'default',
                        color: activeTab === 7 ? 'white' : 'text.primary'
                      }}
                    >
                      Prescription
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(8)}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 800,
                        backgroundColor: activeTab === 8 ? 'primary.main' : 'default',
                        color: activeTab === 8 ? 'white' : 'text.primary'
                      }}
                    >
                      Family History
                    </Button>
                  </Box>

                  {activeTab === 0 && <></>}
                  {activeTab === 1 && (
                    <>
                      <Button
                        sx={{ marginTop: '1%' }}
                        variant="contained"
                        disabled={!['new', 'edit'].includes(currentAction)}
                        onClick={() => setAddDiseaseDialogueOpen(true)}
                      >
                        Add New
                      </Button>
                      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Table
                          containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                          columns={DISEASE_DETAIL_HEADER}
                          data={diseases.map((data,index) => ({ _id: index, ...data })) || []}
                          showDeleteAction={true}
                          onDeleteHandler={handleDiseaseDelete}
                          disabled={!['new', 'edit'].includes(currentAction)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography fontWeight="bold" sx={{ fontSize: '14px', color: 'red' }}>
                          Past Disease History
                        </Typography>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Table
                          containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                          data={diseaseHistory || []}
                          columns={PAST_HISTORY_DISEASE}
                          size="small"
                          onDeleteHandler={handleRowDelete}
                        />
                      </Grid>
                    </>
                  )}
                  {activeTab === 2 && (
                    <>
                      <Button
                        sx={{ marginTop: '1%' }}
                        variant="contained"
                        disabled={!['new', 'edit'].includes(currentAction)}
                        onClick={() => setAddComplaintDialogueOpen(true)}
                      >
                        Add New
                      </Button>
                      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Table
                          containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                          columns={COMPLAINT_DETAIL_HEADER}
                          data={complaints.map((data,index) => ({ _id: index, ...data }))}
                          showDeleteAction={true}
                          onDeleteHandler={handleComplaintDelete}
                          disabled={!['new', 'edit'].includes(currentAction)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography fontWeight="bold" sx={{ fontSize: '14px', color: 'red' }}>
                          Past Complaints History
                        </Typography>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Table
                          containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                          data={complaintHistory || []}
                          columns={PAST_HISTORY_COMPLAINTS}
                          size="small"
                          onDeleteHandler={handleRowDelete}
                        />
                      </Grid>
                    </>
                  )}
                  {activeTab === 3 && (
                    <>
                      <Button
                        variant="contained"
                        sx={{ marginTop: '1%' }}
                        onClick={() => setAddAllergyDialogueOpen(true)}
                        disabled={!['new', 'edit'].includes(currentAction)}
                      >
                        Add Details
                      </Button>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={ALLERGY_DETAIL_HEADER}
                            data={allergy.map((data,index) => ({ _id: index, ...data }))}
                            showDeleteAction={true}
                            onDeleteHandler={handleRowAllergyDelete}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography sx={{ fontWeight: 'bold', color: 'red', fontSize: '14px' }}>
                            Past Allergy Details
                          </Typography>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={PAST_ALLERGY_DETAIL_HEADER}
                            data={allergyHistory || []}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                  {activeTab === 4 && (
                    <>
                      <Button
                        variant="contained"
                        sx={{ marginTop: '1%' }}
                        onClick={() => setGeneralExamDialogueOpen(true)}
                        disabled={!['new', 'edit'].includes(currentAction)}
                      >
                        Add Details
                      </Button>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={GENERAL_EXAM_DETAIL_HEADER}
                            data={generalExam.map((data,index) => ({ _id: index, ...data }))}
                            showDeleteAction={true}
                            onDeleteHandler={handleRowGeneralExamDelete}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography sx={{ fontWeight: 'bold', color: 'red', fontSize: '14px' }}>
                            Past General Examination History
                          </Typography>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={PAST_GENERAL_EXAM_DETAIL_HEADER}
                            data={generalExamHistory || []}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {activeTab === 5 && (
                    <>
                      <Button
                        variant="contained"
                        sx={{ marginTop: '1%' }}
                        onClick={() => setClinicalExamDialogueOpen(true)}
                        disabled={!['new', 'edit'].includes(currentAction)}
                      >
                        Add Details
                      </Button>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={CLINICAL_EXAM_DETAIL_HEADER}
                            data={clinicalExam.map((data,index) => ({ _id: index, ...data }))}
                            showDeleteAction={true}
                            onDeleteHandler={handleRowClinicalExamDelete}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography sx={{ fontWeight: 'bold', color: 'red', fontSize: '14px' }}>
                            Past Clinical Examination History
                          </Typography>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={PAST_CLINICAL_EXAM_DETAIL_HEADER}
                            data={clinicalExamHistory || []}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {activeTab === 6 && (
                    <>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', marginTop: '1%' }}>
                          <Button
                            variant="contained"
                            sx={{ width: '10%' }}
                            onClick={() => setInvestigationDialogOpen(true)}
                            disabled={!['new', 'edit'].includes(currentAction) || !sponsorIDRef.current}
                          >
                            Add Service
                          </Button>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={INVESTIGATION_DETAIL_HEADER}
                            data={investigation.map((data,index) => ({ _id: index, ...data }))}
                            showDeleteAction={true}
                            onDeleteHandler={handleDeleteInvestigation}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography sx={{ fontWeight: 'bold', color: 'red', fontSize: '14px', fontWeight: 600 }}>
                            Past Investigation History
                          </Typography>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={PAST_INVESTIGATION_DETAIL_HEADER}
                            data={investigationHistory || []}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                  {activeTab === 7 && (
                    <>
                      <>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', marginTop: '1%' }}>
                            <Button
                              variant="contained"
                              sx={{ width: '10%' }}
                              onClick={() => setAddMedicineDialogue(true)}
                              disabled={!['new', 'edit'].includes(currentAction)}
                            >
                              Add Medicine
                            </Button>
                            <Table
                              containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                              columns={MEDICINE_DETAIL_HEADER}
                              data={medicineDetailList.map((data,index) => ({ _id: index, ...data })) || []}
                              showDeleteAction={true}
                              onDeleteHandler={handleDeleteMedicine}
                              disabled={!['new', 'edit'].includes(currentAction)}
                            />
                          </Grid>
                          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography sx={{ fontWeight: 'bold', color: 'red', fontSize: '14px', fontWeight: 600 }}>
                              Past Medicine History
                            </Typography>
                            <Table
                              containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                              columns={PAST_MEDICINE_DETAIL_HEADER}
                              data={prescriptionHistory || []}
                              disabled={!['new', 'edit'].includes(currentAction)}
                            />
                          </Grid>
                        </Grid>
                      </>
                    </>
                  )}

                  {activeTab === 8 && (
                    <>
                      <Button
                        variant="contained"
                        sx={{ marginTop: '1%' }}
                        onClick={() => setFamilyHistoryDialogueOpen(true)}
                        disabled={!['new', 'edit'].includes(currentAction)}
                      >
                        Add Details
                      </Button>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={FAMILY_HISTORY_DETAIL_HEADER}
                            data={familyHistory.map((data,index) => ({ _id: index, ...data }))}
                            showDeleteAction={true}
                            onDeleteHandler={handleRowFamilyHistoryDelete}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography sx={{ fontWeight: 'bold', color: 'red', fontSize: '14px' }}>
                            Past Family History
                          </Typography>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={PAST_FAMILY_HISTORY_DETAIL_HEADER}
                            data={pastFamilyHistory || []}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Patient History Query Window
          </Typography>

          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '1.4rem' // Adjust input font size
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 1.4rem'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem' // Adjust label font size
              }
            }}
            label="Search Patient"
            variant="outlined"
            value={queryInput}
            onChange={handleQueryChange}
          />
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_HEADER}
          data={patientDataObj?.data|| []}
          loading={patientDataObj.loading}
          onRowClick={(row) => {
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
      <Dialog
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        open={searchPatientRegisterationDialog}
        onClose={() => setSearchPatientRegisterationDialog(false)}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Patient Query Window
          </Typography>
          <IconButton onClick={() => setSearchPatientRegisterationDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={PATIENT_TABLE_HEADER}
          data={patientDetailList || []}
          onRowClick={(row) => {
            searchHistoryDetails(row?.uhid)
            setValue('uhid', row.uhid);
            setValue('visit_no', row.visit_no);
            setValue('op', row.op_no);
            setValue('patient_name', `${row.first_name} ${row.last_name}`);
            setValue('patient_suffix', row.name_suffix);
            setValue('opd_type', row.opd_type?.type);
            setValue('ref_type', row.ref_by?.referral_name);
            setValue('application_no', row.app_no_date);
            setValue('doctor', row.consultant?.name);
            doctorIDRef.current = row.consultant?._id;
            setValue('department', row.department?.department);
            setValue('sponsor', row.sponsor?.name);
            sponsorIDRef.current = row.sponsor?._id;
            setValue('remarks', row.remarks);
            setValue('queue_no', row.app_no_date);
            setValue('card_no', row.chq_card_no);
            setValue('vertical', row.vertical?.verticalname);
            setValue('patient_age', calculateAge(row.dob));
            setValue('mobile', row?.address?.mobile);
            setSearchPatientRegisterationDialog(false);
            setPatientVitals(row || {});
            setPastComplaints(row || {});
            setPastDiagnos(row || {});
            setPastService(row || {});
            setPastMedicine(row || {});
          }}
        />
      </Dialog>
      <Dialog
        open={addMedicineDialogue}
        onClose={() => setAddMedicineDialogue(false)}
        PaperProps={{ sx: { p: 2, width: '35rem' } }}
        maxWidth="xl"
        scroll="body"
      >
        <DischargeSummaryMedicineDetailsForm
          medicine={medicine}
          dose={dose}
          frequency={frequency}
          setMedicine={setMedicine}
          onClose={() => setAddMedicineDialogue(false)}
          onAdd={onAddDetailHandler}
        />
      </Dialog>
      <Dialog
        open={addDiseaseDialogueOpen}
        onClose={() => setAddDiseaseDialogueOpen(false)}
        PaperProps={{ sx: { p: 2, width: '45rem' } }}
        maxWidth="xl"
        scroll="body"
      >
        <DiseaseDetailsForm
          onClose={() => setAddDiseaseDialogueOpen(false)}
          onAdd={(row) => setDiseases((prev) => [...prev, row])}
          setAddDiseaseDialogueOpen={setAddDiseaseDialogueOpen}
        />
      </Dialog>
      <Dialog
        open={addAlleryDialogueOpen}
        onClose={() => setAddAllergyDialogueOpen(false)}
        PaperProps={{ sx: { p: 2, width: '45rem' } }}
        maxWidth="xl"
        scroll="body"
      >
        <AllergyDetailsForm
          onClose={() => setAddAllergyDialogueOpen(false)}
          onAdd={(row) => setAllergy((prev) => [...prev, row])}
          setAddAllergyDialogueOpen={setAddAllergyDialogueOpen}
        />
      </Dialog>

      <Dialog
        open={generalExamDialogueOpen}
        onClose={() => setGeneralExamDialogueOpen(false)}
        PaperProps={{ sx: { p: 2, width: '45rem' } }}
        maxWidth="xl"
        scroll="body"
      >
        <GeneralExaminationDetailsForm
          onClose={() => setGeneralExamDialogueOpen(false)}
          onAdd={(row) => setGeneralExam((prev) => [...prev, row])}
          setGeneralExamDialogueOpen={setGeneralExamDialogueOpen}
        />
      </Dialog>

      <Dialog
        open={clinicalExamDialogueOpen}
        onClose={() => setClinicalExamDialogueOpen(false)}
        PaperProps={{ sx: { p: 2, width: '45rem' } }}
        maxWidth="xl"
        scroll="body"
      >
        <ClinicalExamDetailsForm
          onClose={() => setClinicalExamDialogueOpen(false)}
          onAdd={(row) => setClinicalExam((prev) => [...prev, row])}
          setClinicalExamDialogueOpen={setClinicalExamDialogueOpen}
        />
      </Dialog>

      <Dialog
        open={familyHistoryDialogueOpen}
        onClose={() => setFamilyHistoryDialogueOpen(false)}
        PaperProps={{ sx: { p: 2, width: '45rem' } }}
        maxWidth="xl"
        scroll="body"
      >
        <FamilyHistoryDetailsForm
          onClose={() => setFamilyHistoryDialogueOpen(false)}
          onAdd={(row) => setFamilyHistory((prev) => [...prev, row])}
          setFamilyHistoryDialogueOpen={setFamilyHistoryDialogueOpen}
        />
      </Dialog>

      <Dialog
        open={investigationDialogOpen}
        onClose={() => setInvestigationDialogOpen(false)}
        PaperProps={{ sx: { p: 2, width: '100rem', height: '80vh' } }}
        maxWidth="xl"
        scroll="body"
      >
        <InvestigationBookingDetailsForm
          onClose={() => setInvestigationDialogOpen(false)}
          onAdd={onDetailAddHandlers}
          setInvestigationDialogOpen={setInvestigationDialogOpen}
          sponsorID={sponsorIDRef.current}
          doctorID={doctorIDRef.current}
        />
      </Dialog>

      <Dialog
        open={addComplaintDialogueOpen}
        onClose={() => setAddComplaintDialogueOpen(false)}
        PaperProps={{ sx: { p: 2, width: '50rem', height: '40vh' } }}
        maxWidth="xl"
        scroll="body"
      >
        <ComplaintsDetailForm
          onClose={() => setAddComplaintDialogueOpen(false)}
          onAdd={(row) => setComplaints((prev) => [...prev, row])}
          setAddComplaintDialogueOpen={setAddComplaintDialogueOpen}
        />
      </Dialog>
    </>
  );
};
